import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Checkbox, Select, useToast, Icon, Card, Input, Tooltip } from '@aidkitorg/component-library';
import { SelectOptionType } from '@aidkitorg/component-library/dist/types/src/Select';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../Localization';
import { useUserContext } from '../../Contexts/UserContext';
import { useOrganizationContext } from '../../Contexts/OrganizationContext';
import { type UserGroup } from '@aidkitorg/aidkit/lib/users/UserGroupService';
import { useTagDetails } from '../../Admin/TagsUtil';
import { isPermissionTag, isRestrictionTag } from '@aidkitorg/types/lib/tags';
import { AdminPanelPage } from '../AdminPanelPage';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import InterfaceContext from '../../Context';

type CreatedGroup = {
  id?: number;
  name?: string;
  description?: string;
  organization?: string | null;
  permissions?: string;
  metadata?: any;
};

// Component to render individual tag checkbox with details
const TagCheckbox = ({
  tag,
  checked,
  onChange
}: {
  tag: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  const tagDetails = useTagDetails(tag);

  return (
    <div className="w-full [&>*]:max-w-none [&>*]:w-full">
      <Checkbox
        id={`permission-${tag}`}
        label={tagDetails.name}
        description={tagDetails.description}
        checked={checked}
        onChange={(checkedValue) => {
          // Handle both boolean and ChangeEvent
          const isChecked = typeof checkedValue === 'boolean' ? checkedValue : checkedValue.target.checked;
          onChange(isChecked);
        }}
        variant="tile"
      />
    </div>
  );
};

// Component to display assigned tag in view mode
const AssignedTagDisplay = ({ tag }: { tag: string }) => {
  const tagDetails = useTagDetails(tag);

  return (
    <div className="p-3 rounded-lg border">
      <div className="font-medium text-sm text-gray-900">{tagDetails.name}</div>
      <div className="text-sm text-gray-600 mt-1">{tagDetails.description}</div>
    </div>
  );
};

export const UserGroupDetailPage: React.FC = () => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { groupId } = useParams<{ groupId?: string }>();
  const { toast } = useToast();

  const isEditMode = groupId && groupId !== 'new';
  const { selectedOrganization, organizations } = useOrganizationContext();
  const { loadUserGroups, createUserGroup, updateUserGroup, editableTags, loadEditableTags, getUserGroupById, applicantGroups, applicantGroupsRequireSelection, loadApplicantGroups } = useUserContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    applicantGroups?: boolean;
  }>({});

  // Page-level edit mode (separate from create vs update mode)
  const [isPageEditMode, setIsPageEditMode] = useState(!isEditMode); // Default to edit mode for new groups, view mode for existing

  // Access config for applicant groups
  const interfaceContext = useContext(InterfaceContext);
  const [currentUserGroup, setCurrentUserGroup] = useState<UserGroup | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organization: null as string | null,
    permissions: [] as string[],
    applicantGroups: [] as string[]
  });

  // Track initial form state for unsaved changes detection
  const [initialFormState, setInitialFormState] = useState<{
    name: string;
    description: string;
    organization: string | null;
    permissions: string[];
    applicantGroups: string[];
  } | null>(null);

  // Load editable tags and applicant groups once on mount
  useEffect(() => {
    loadEditableTags();
    loadApplicantGroups();
  }, [loadEditableTags, loadApplicantGroups]);

  // Initialize form data - fetch user group by ID if editing, or set defaults if creating new
  useEffect(() => {
    if (isEditMode && groupId) {
      // Fetch the specific user group by ID
      getUserGroupById(parseInt(groupId)).then(userGroup => {
        if (userGroup) {
          setCurrentUserGroup(userGroup);
          const parsedMetadata = userGroup.metadata ? userGroup.metadata : {};
          // Include both current and orphaned groups in applicantGroups
          const storedApplicantGroups = Array.isArray(parsedMetadata.applicantGroups)
            ? parsedMetadata.applicantGroups
            : (parsedMetadata.applicantGroups ? Object.keys(parsedMetadata.applicantGroups) : []);

          const formState = {
            name: userGroup.name || '',
            description: userGroup.description || '',
            organization: userGroup.organization || null,
            permissions: userGroup.permissions ? userGroup.permissions.split(',') : [],
            applicantGroups: storedApplicantGroups
          };

          setFormData(formState);
          setInitialFormState(formState);
        }
      });
    } else if (!isEditMode) {
      // Creating new group
      const newFormState = {
        name: '',
        description: '',
        organization: selectedOrganization?.uid || null,
        permissions: [],
        applicantGroups: []
      };

      setFormData(newFormState);
      setInitialFormState(newFormState);
    }
  }, [isEditMode, groupId, getUserGroupById, selectedOrganization]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePermissionChange = (permissionValue: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, permissionValue]
        : prev.permissions.filter(p => p !== permissionValue)
    }));
  };

  const handleApplicantGroupChange = (applicantGroupValue: string, checked: boolean) => {
    const newApplicantGroups = checked
      ? [...formData.applicantGroups, applicantGroupValue]
      : formData.applicantGroups.filter(f => f !== applicantGroupValue);

    setFormData(prev => ({
      ...prev,
      applicantGroups: newApplicantGroups
    }));

    // Real-time validation for applicant groups
    const shouldShowError = applicantGroupsRequireSelection && newApplicantGroups.length === 0;
    setValidationErrors(prev => ({
      ...prev,
      applicantGroups: shouldShowError
    }));
  };


  // Organization field logic
  const shouldShowOrganizationField = organizations.length > 0;
  const shouldDisableOrganizationField = selectedOrganization !== undefined || organizations.length === 1;

  const organizationOptions: SelectOptionType[] = [
    { label: L.admin_panel.user_management.no_organization, value: '' },
    ...organizations.map(org => ({
      label: org.name,
      value: org.uid,
    }))
  ];

  const getSelectedOrganizationOption = () => {
    if (formData.organization) {
      return organizationOptions.find(opt => opt.value === formData.organization) || undefined;
    }
    // Return the "No Organization" option when organization is null/empty
    return organizationOptions.find(opt => opt.value === '') || undefined;
  };

  const handleOrganizationChange = (option: SelectOptionType | null) => {
    setFormData(prev => ({
      ...prev,
      organization: (option?.value && option.value !== '') ? option.value : null
    }));
  };

  // Helper to check if an applicant group is orphaned (not in current config)
  const isOrphanedGroup = (tag: string) => {
    return !applicantGroups.some(group => group.tag === tag);
  };


  // Get all groups (current config + orphaned ones from initial state)
  const allApplicantGroups = [
    ...applicantGroups,
    // Add orphaned groups that exist in initialFormState but not in current config
    ...(initialFormState?.applicantGroups || [])
      .filter(tag => isOrphanedGroup(tag))
      .map(tag => ({
        tag,
        name: { [interfaceContext.lang]: tag }, // Use tag as display name
        description: { [interfaceContext.lang]: L.admin_panel.user_groups.stale_group_description }
      }))
  ];


  const isFormValid = () => {
    // Basic validation: name and at least one permission required
    const hasName = formData.name.trim() !== '';
    const hasPermissions = formData.permissions.length > 0;

    // Applicant group validation: if selection is required, at least one must be selected
    const applicantGroupsValid = !applicantGroupsRequireSelection || formData.applicantGroups.length > 0;

    return hasName && hasPermissions && applicantGroupsValid;
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = initialFormState ? (
    formData.name !== initialFormState.name ||
    formData.description !== initialFormState.description ||
    formData.organization !== initialFormState.organization ||
    JSON.stringify(formData.permissions.sort()) !== JSON.stringify(initialFormState.permissions.sort()) ||
    JSON.stringify(formData.applicantGroups.sort()) !== JSON.stringify(initialFormState.applicantGroups.sort())
  ) : false;

  const handleSubmit = useCallback(async () => {
    // Clear previous validation errors
    setValidationErrors({});

    // Validate form and show specific errors
    const errors: { applicantGroups?: boolean } = {};

    if (applicantGroupsRequireSelection && formData.applicantGroups.length === 0) {
      errors.applicantGroups = true;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    if (!isFormValid() || isSubmitting) return; // Prevent double submission

    setIsSubmitting(true);
    try {
      let success = false;
      let createdGroup: CreatedGroup | null = null;

      const submitData = {
        name: formData.name,
        description: formData.description || null,
        permissions: formData.permissions.join(','), // Convert array to comma-separated string
        organization: formData.organization,
        metadata: {
          applicantGroups: formData.applicantGroups
        }
      };

      if (isEditMode && groupId) {
        success = await updateUserGroup(parseInt(groupId), submitData);
        if (success) {
          // Update initialFormState to reflect saved changes
          setInitialFormState({
            ...formData
          });
          toast({
            title: 'Success',
            description: 'User group updated successfully',
            variant: 'success'
          });
        }
      } else {
        const result = await createUserGroup(submitData);
        success = result.success;
        createdGroup = result.group;
        if (success) {
          toast({
            title: 'Success',
            description: 'User group created successfully',
            variant: 'success'
          });
        }
      }

      if (success) {
        // Reload user groups to get the updated data
        loadUserGroups(formData.organization || selectedOrganization?.uid);

        if (isEditMode) {
          // For existing groups, stay on the page and exit edit mode
          setIsPageEditMode(false);
        } else if (createdGroup && createdGroup.id) {
          const groupId = createdGroup.id;
          // For new groups, update the current page state to reflect the created group
          const parsedCreatedMetadata = createdGroup.metadata ? createdGroup.metadata : {};
          // Include both current and orphaned groups for created group
          const storedCreatedApplicantGroups = Array.isArray(parsedCreatedMetadata.applicantGroups)
            ? parsedCreatedMetadata.applicantGroups
            : (parsedCreatedMetadata.applicantGroups ? Object.keys(parsedCreatedMetadata.applicantGroups) : []);

          const newFormState = {
            name: createdGroup.name || '',
            description: createdGroup.description || '',
            organization: createdGroup.organization || null,
            permissions: createdGroup.permissions ? createdGroup.permissions.split(',') : [],
            applicantGroups: storedCreatedApplicantGroups
          };

          setFormData(newFormState);
          setInitialFormState({
            ...newFormState
          });
          setIsPageEditMode(false); // Switch to view mode

          // Navigate to the created group's page
          setTimeout(() => {
            history.push(`/admin-panel/user-management/user-groups/${groupId}`);
          }, 100);
        } else {
          // Fallback: stay on current page and exit edit mode
          setIsPageEditMode(false);
        }
      }
    } catch (error) {
      console.error('Error saving user group:', error);
      toast({
        title: 'Error',
        description: 'Failed to save user group. Please try again.',
        variant: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [isEditMode, groupId, formData, updateUserGroup, createUserGroup, loadUserGroups, selectedOrganization, history, toast, isFormValid, isSubmitting]);

  const handleCancel = () => {
    history.push('/admin-panel/user-management/user-groups');
  };

  const pageTitle = initialFormState?.name || L.admin_panel.user_groups.create_user_group;

  // Determine if user group can be edited
  const canEditGroup = !isEditMode || (currentUserGroup?.canEdit !== false);

  // Page action buttons based on mode
  const pageActionButtons = [];

  if (isEditMode && !isPageEditMode) {
    // View mode - show Edit button
    if (canEditGroup) {
      pageActionButtons.push(
        <Button
          key="edit-group"
          onClick={() => setIsPageEditMode(true)}
          icon={<Icon name="PencilIcon" type="outline" role="decorative" />}
          variant="secondary"
        >
          {L.admin_panel.user_groups.edit_user_group}
        </Button>
      );
    } else {
      pageActionButtons.push(
        <Tooltip key="edit-group-disabled" text={L.user.edit_user_admin_only} asChild={true}>
          <Button
            icon={<Icon name="PencilIcon" type="outline" role="decorative" />}
            variant="secondary"
            isDisabled={true}
          >
            {L.admin_panel.user_groups.edit_user_group}
          </Button>
        </Tooltip>
      );
    }
  }

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.user_management
    },
    {
      label: L.admin_panel.user_groups.title,
      href: '/admin-panel/user-management/user-groups'
    },
    {
      label: isEditMode ? L.admin_panel.user_groups.view_user_group : L.admin_panel.user_groups.create_user_group
    }
  ];

  // Group tags by category (permissions vs restrictions)
  const permissionsByCategory = React.useMemo(() => {
    const permissions: string[] = [];
    const restrictions: string[] = [];

    editableTags.forEach(tag => {
      if (isPermissionTag(tag)) {
        permissions.push(tag);
      } else if (isRestrictionTag(tag)) {
        restrictions.push(tag);
      } else {
        // Custom tags go to permissions by default
        permissions.push(tag);
      }
    });

    return {
      Permissions: permissions,
      Restrictions: restrictions
    };
  }, [editableTags]);

  return (
    <>
      <Prompt
        when={hasUnsavedChanges}
        message={L.admin_panel.reports.unsaved_changes_warning}
      />

      <AdminPanelPage
        title={pageTitle}
        pageActionButtons={pageActionButtons}
        breadcrumbs={breadcrumbs}
      >
        <div className="w-full max-w-6xl">
          <Card className="w-full mt-10">
            <div className="space-y-6">

              {/* Group Name - show in form when creating new groups or editing existing groups */}
              {(!isEditMode || (isEditMode && isPageEditMode)) && (
                <div>
                  <Input
                    label={L.admin_panel.user_groups.group_name}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    placeholder="Enter group name"
                  />
                </div>
              )}

              {/* Description */}
              {isPageEditMode || !isEditMode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {L.admin_panel.user_groups.description}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={L.admin_panel.user_groups.optional_description}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {L.admin_panel.user_groups.description}
                  </label>
                  <div className="text-gray-900">
                    {formData.description || (
                      <span className="text-gray-400 italic">{L.admin_panel.user_groups.no_description}</span>
                    )}
                  </div>
                </div>
              )}

              {shouldShowOrganizationField && (
                isPageEditMode ? (
                  <Select
                    label={L.admin_panel.user_management.organization}
                    labelLocation="above"
                    options={organizationOptions}
                    value={getSelectedOrganizationOption()}
                    onChange={handleOrganizationChange}
                    disabled={shouldDisableOrganizationField}
                    description=""
                    className="w-full"
                  />
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {L.admin_panel.user_management.organization}
                    </label>
                    <div className="text-gray-900">
                      {getSelectedOrganizationOption()?.label || L.admin_panel.user_management.no_organization_assigned}
                    </div>
                  </div>
                )
              )}

              <div>
                <div className="space-y-8">
                  {/* Permissions Section */}
                  {permissionsByCategory.Permissions.length > 0 && (
                    <div>
                      <div className="flex gap-3 items-center">
                        <label className="font-medium text-gray-900 pt-2">{L.admin_panel.user_groups.permissions}</label>
                      </div>

                      {!isPageEditMode ? (
                        // View mode - show only assigned permissions
                        <div>
                          {permissionsByCategory.Permissions.filter(tag => formData.permissions.includes(tag)).length > 0 ? (
                            <div className="space-y-2">
                              {permissionsByCategory.Permissions.filter(tag => formData.permissions.includes(tag)).map((tag) => (
                                <AssignedTagDisplay key={tag} tag={tag} />
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500 border rounded-lg">
                              <p>{L.admin_panel.user_groups.no_permissions_assigned}</p>
                              <p className="text-sm mt-1">{L.admin_panel.user_groups.click_edit_group_to_add_permissions}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Edit mode - show all checkboxes
                        <div className="space-y-2">
                          {permissionsByCategory.Permissions.map((tag) => (
                            <TagCheckbox
                              key={tag}
                              tag={tag}
                              checked={formData.permissions.includes(tag)}
                              onChange={(checked) => handlePermissionChange(tag, checked)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Restrictions Section */}
                  {permissionsByCategory.Restrictions.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3">
                        <label className="font-medium text-gray-900 pt-2">{L.admin_panel.user_groups.restrictions}</label>
                      </div>

                      {!isPageEditMode ? (
                        // View mode - show only assigned restrictions
                        <div>
                          {permissionsByCategory.Restrictions.filter(tag => formData.permissions.includes(tag)).length > 0 ? (
                            <div className="space-y-2">
                              {permissionsByCategory.Restrictions.filter(tag => formData.permissions.includes(tag)).map((tag) => (
                                <AssignedTagDisplay key={tag} tag={tag} />
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500 border rounded-lg">
                              <div>{L.admin_panel.user_groups.no_restrictions_assigned}</div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Edit mode - show all checkboxes
                        <div className="space-y-2">
                          {permissionsByCategory.Restrictions.map((tag) => (
                            <TagCheckbox
                              key={tag}
                              tag={tag}
                              checked={formData.permissions.includes(tag)}
                              onChange={(checked) => handlePermissionChange(tag, checked)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Applicant Groups Section */}
                  {(allApplicantGroups.length > 0) && (
                    <div>
                      <div className="flex items-center gap-3">
                        <label className="font-medium text-gray-900 pt-2">
                          Limited to Applicants
                          {applicantGroupsRequireSelection && <span className="text-red-500 ml-1">*</span>}
                        </label>
                      </div>

                      {!isPageEditMode ? (
                        // View mode - show assigned applicant groups
                        <div>
                          {formData.applicantGroups.length > 0 ? (
                            <div className="space-y-2">
                              {allApplicantGroups
                                .filter((applicantGroup: any) => formData.applicantGroups.includes(applicantGroup.tag))
                                .map((applicantGroup: any) => {
                                  const isStale = isOrphanedGroup(applicantGroup.tag);
                                  return (
                                    <div key={applicantGroup.tag} className={`p-3 rounded-lg border ${
                                      isStale ? 'border-amber-200 bg-amber-50' : ''
                                    }`}>
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className={`font-medium text-sm ${
                                            isStale ? 'text-amber-800' : 'text-gray-900'
                                          }`}>
                                            {applicantGroup.name[interfaceContext.lang]}
                                          </div>
                                          <div className={`text-sm mt-1 ${
                                            isStale ? 'text-amber-700' : 'text-gray-600'
                                          }`}>
                                            {applicantGroup.description[interfaceContext.lang]}
                                          </div>
                                        </div>
                                        {isStale && (
                                          <div className="ml-2">
                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-800">
                                              Stale
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-500 border rounded-lg">
                              <p>{L.admin_panel.user_groups.no_applicant_groups_applied}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Edit mode - show all checkboxes with stale indicators
                        <div>
                          <p className="text-sm text-gray-600 mb-4">
                            {L.admin_panel.user_groups.applicant_groups_description}
                          </p>
                          <div className="space-y-2">
                            {allApplicantGroups.map((applicantGroup: any) => {
                              const isStale = isOrphanedGroup(applicantGroup.tag);
                              const displayLabel = isStale
                                ? `${applicantGroup.name[interfaceContext.lang]} ${L.admin_panel.user_groups.stale_group_suffix}`
                                : applicantGroup.name[interfaceContext.lang];

                              return (
                                <div key={applicantGroup.tag} className="w-full [&>*]:max-w-none [&>*]:w-full">
                                  <Checkbox
                                    id={`applicant-group-${applicantGroup.tag}`}
                                    label={displayLabel}
                                    description={applicantGroup.description[interfaceContext.lang]}
                                    checked={formData.applicantGroups.includes(applicantGroup.tag)}
                                    onChange={(checkedValue) => {
                                      const isChecked = typeof checkedValue === 'boolean' ? checkedValue : checkedValue.target.checked;
                                      handleApplicantGroupChange(applicantGroup.tag, isChecked);
                                    }}
                                    variant="tile"
                                  />
                                </div>
                              );
                            })}
                          </div>
                          {validationErrors.applicantGroups && (
                            <p className="text-sm text-red-600 mt-3 font-medium">
                              {L.admin_panel.user_groups.applicant_groups_required_error}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Save and Cancel buttons - only show when in edit mode */}
              {isPageEditMode && (
                <div className="flex justify-end gap-3 pt-6 border-t">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (isEditMode) {
                        setIsPageEditMode(false);
                      } else {
                        handleCancel();
                      }
                    }}
                  >
                    {L.apply.cancel}
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    isDisabled={!isFormValid() || isSubmitting}
                    loading={isSubmitting}
                  >
                    {isEditMode ? L.admin_panel.user_groups.save_changes : L.admin_panel.user_groups.create_group}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </AdminPanelPage>
    </>
  );
};

export default UserGroupDetailPage;
